// Client side of the `/map/` and `/imagery/` namespaces: what a browser needs
// to point a tile layer at cdn.vortexnav.com, and to read back what the CDN is
// willing to serve.
//
// The terms gate lives here as well as in the Worker, and on purpose. A
// provider whose licence we have not read resolves to its own template, so a
// client asks the CDN only for what the CDN would actually serve. One switch
// in the registry moves a provider across, and nothing else changes.
//
// Framework-neutral — Leaflet, MapLibre and the canvas thumbnail renderer in
// vortex-track all take a `{z}/{x}/{y}` template string.

import {
  MAP_PROVIDERS,
  MAP_ROUTE_VERSION,
  mapProviderById,
  onPublicCdn,
  type MapNamespace,
  type MapProvider,
  type TermsStatus,
} from './providers';

export const DEFAULT_CDN_URL = 'https://cdn.vortexnav.com';

function normalizeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, '');
}

function requireProvider(providerId: string): MapProvider {
  const provider = mapProviderById(providerId);
  if (!provider) throw new Error(`Unknown basemap provider: ${providerId}`);
  return provider;
}

/**
 * A `{z}/{x}/{y}` template on the CDN. Always XYZ, whatever order the provider
 * upstream wants — the Worker re-orders. The namespace comes from the registry:
 * `/map/` for rendered cartography, `/imagery/` for satellite and aerial.
 */
export function tileTemplate(providerId: string, baseUrl = DEFAULT_CDN_URL): string {
  const p = requireProvider(providerId);
  return `${normalizeBaseUrl(baseUrl)}/${p.namespace}/${p.id}/${MAP_ROUTE_VERSION}/{z}/{x}/{y}.${p.extension}`;
}

/**
 * The provider's own template, for talking to it directly.
 *
 * The fallback for a build with no CDN configured, and the standing answer for
 * any provider whose terms are not yet confirmed. Anything using it is hitting
 * the provider from the browser, with no cache of ours in front and no
 * attribution control — the state the CDN namespaces exist to end.
 */
export function directTileTemplate(providerId: string): string {
  return requireProvider(providerId).upstream;
}

/** One concrete tile URL — for renderers that build their own requests. */
export function tileUrl(
  providerId: string,
  z: number,
  x: number,
  y: number,
  baseUrl: string | null = DEFAULT_CDN_URL,
): string {
  return resolveTemplate(providerId, baseUrl)
    .replace('{z}', String(z))
    .replace('{x}', String(x))
    .replace('{y}', String(y));
}

/**
 * Whether this provider goes through the CDN right now: a CDN is configured
 * AND its licence has been read and permits proxying.
 */
export function servedByCdn(providerId: string, baseUrl: string | null): boolean {
  return baseUrl !== null && onPublicCdn(requireProvider(providerId));
}

/** The template a client should actually use, CDN or direct. */
export function resolveTemplate(providerId: string, baseUrl: string | null): string {
  return servedByCdn(providerId, baseUrl)
    ? tileTemplate(providerId, baseUrl as string)
    : directTileTemplate(providerId);
}

/** What a Leaflet `TileLayer` needs, registry values included. */
export interface TileLayerOptions {
  url: string;
  attribution: string;
  maxZoom: number;
  maxNativeZoom: number;
  /** True when the URL points at our CDN rather than the provider. */
  viaCdn: boolean;
}

/**
 * Layer options for a provider. `baseUrl` null means no CDN is configured; a
 * provider whose terms are unconfirmed stays direct regardless.
 */
export function tileLayerOptions(
  providerId: string,
  baseUrl: string | null = DEFAULT_CDN_URL,
): TileLayerOptions {
  const p = requireProvider(providerId);
  return {
    url: resolveTemplate(p.id, baseUrl),
    attribution: p.attribution,
    maxZoom: p.maxZoom,
    maxNativeZoom: p.maxNativeZoom,
    viaCdn: servedByCdn(p.id, baseUrl),
  };
}

/** One entry of the providers manifest — the registry minus anything upstream. */
export interface PublishedMapProvider {
  id: string;
  label: string;
  namespace: MapNamespace;
  kind: 'base' | 'overlay';
  extension: 'png' | 'jpg';
  maxZoom: number;
  maxNativeZoom: number;
  attribution: string;
  /** Whether the CDN serves it today; false = clients must go direct. */
  proxied: boolean;
  /** Why it is served, or why not — `prototype` is not a licence review. */
  termsStatus: TermsStatus;
  /** Why not, when it is not. Published so the gap is visible, not silent. */
  termsNote: string;
  /** Present only when `proxied` — there is no CDN URL otherwise. */
  template?: string;
}

/** Strip every upstream host and credential reference before publishing. */
export function publishedProvider(provider: MapProvider, baseUrl: string): PublishedMapProvider {
  return {
    id: provider.id,
    label: provider.label,
    namespace: provider.namespace,
    kind: provider.kind,
    extension: provider.extension,
    maxZoom: provider.maxZoom,
    maxNativeZoom: provider.maxNativeZoom,
    attribution: provider.attribution,
    proxied: onPublicCdn(provider),
    termsStatus: provider.termsStatus,
    termsNote: provider.termsNote,
    ...(onPublicCdn(provider) ? { template: tileTemplate(provider.id, baseUrl) } : {}),
  };
}

/** Every provider but the prototypes, which are front door only, optionally narrowed to one namespace. */
export function publishedProviders(baseUrl: string, namespace?: MapNamespace): PublishedMapProvider[] {
  return MAP_PROVIDERS
    .filter((p) => p.termsStatus !== 'prototype' && (!namespace || p.namespace === namespace))
    .map((p) => publishedProvider(p, baseUrl));
}

export async function fetchMapProviders(
  baseUrl = DEFAULT_CDN_URL,
  namespace: MapNamespace = 'map',
): Promise<PublishedMapProvider[]> {
  const response = await fetch(`${normalizeBaseUrl(baseUrl)}/meta/${namespace}/providers`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Map providers failed (${response.status})`);
  const data = await response.json() as { providers?: PublishedMapProvider[] };
  if (!Array.isArray(data.providers)) throw new Error('Map provider manifest is invalid');
  return data.providers;
}
